<template>
    <app-layout title="View Order">
        <page-header>
            <template #title>View Order</template>

            <template #actions>
                <template v-if="isAdmin">
                    <div class="flex flex-col sm:flex-row justify-between gap-x-4 gap-y-2">
                        <!-- Business Central Sync button -->
                        <jet-button @click="orderSync" :disabled="bcSyncProcessing" >
                            BC Sync
                        </jet-button>

                        <!-- Email button / dropdown -->
                        <jet-tertiary-button v-if="showEmailButton" type="button"
                            class="py-1.5 sm:py-1 leading-none"
                            @click="emailOrder('confirmation')"
                        >
                            <img src="/images/icons/email.svg" width="16" height="16" alt="Print">
                        </jet-tertiary-button>

                        <jet-dropdown v-else align="right" width="48">
                            <template #trigger>
                                <jet-tertiary-button size="small">
                                    <img src="/images/icons/email.svg" width="16" height="16" alt="Print">
                                    <img class="ml-2" src="/images/icons/chevron-down.svg" width="16" height="16" alt="Print">
                                </jet-tertiary-button>
                            </template>
                            <template #content>
                                <div class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="emailOrder('confirmation')">Order Confirmation</div>
                                <div v-if="order.is_wholesale" class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="emailOrder('invoice')">Order Invoice</div>
                                <div v-else class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="emailOrder('receipt')">Order Receipt</div>
                                <!-- Temporarily disabled — uncomment when feature is ready -->
                                <!-- <div class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="emailOrder('refund')">Order Refund</div> -->
                            </template>
                        </jet-dropdown>

                        <!-- Print button / dropdown -->
                        <!-- Retail -->
                        <jet-dropdown v-if="!order.is_wholesale" align="right" width="24">
                            <template #trigger>
                                <jet-tertiary-button size="small">
                                    <img src="/images/icons/print.svg" width="16" height="16" alt="Print">
                                    <img class="ml-2" src="/images/icons/chevron-down.svg" width="16" height="16" alt="Print">
                                </jet-tertiary-button>
                            </template>
                            <template #content>
                                <div class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handleRetailPrint('invoices')">Order</div>
                                <div class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handleRetailPrint('labels')">Label</div>
                            </template>
                        </jet-dropdown>

                        <!-- Wholesale -->
                        <jet-dropdown v-else-if="order.status == 2" align="right" width="48">
                            <template #trigger>
                                <jet-tertiary-button size="small">
                                    <img src="/images/icons/print.svg" width="16" height="16" alt="Print">
                                    <img class="ml-2" src="/images/icons/chevron-down.svg" width="16" height="16" alt="Print">
                                </jet-tertiary-button>
                            </template>
                            <template #content>
                                <div class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handlePrint('confirmation')">Order Confirmation</div>
                                <div class="px-4 py-2 hover:bg-gray cursor-pointer text-sm" @click="handlePrint('invoice')">Order Invoice</div>
                            </template>
                        </jet-dropdown>

                        <jet-tertiary-button v-else type="button" class="py-1.5 sm:py-1 leading-none" @click="handlePrint('confirmation')">
                            <img src="/images/icons/print.svg" width="16" height="16" alt="Print">
                        </jet-tertiary-button>
                    </div>

                    <div class="flex flex-col sm:flex-row justify-between gap-x-4 gap-y-2">
                        <jet-button
                            type="button"
                            v-if="order.status === 2"
                            :href="route('refunds.create', { token: order.refund_token })"
                        >
                            Refund
                        </jet-button>

                        <!-- This enables manual picking mode which is how orders were picked before the new picking system was implemented (allows for substitutions and shorts) -->
                        <jet-button v-if="!order.is_wholesale && allowEdit" type="button" class="leading-none inline-flex items-center" :href="manualPickingModeUrl">
                            Edit
                        </jet-button>

                        <jet-button v-else-if="allowEdit" type="button" class="leading-none inline-flex items-center" :href="editUrl">
                            Edit
                        </jet-button>

                        <jet-tertiary-button type="button" class="leading-none" :href="backUrl">
                            Close
                        </jet-tertiary-button>
                    </div>
                </template>

                <jet-tertiary-button v-else type="button" class="leading-none" :href="backUrl">
                    Close
                </jet-tertiary-button>
            </template>
        </page-header>

        <div class="flex flex-col px-3 md:px-4 lg:px-8 gap-y-4 ">
            <order-details :order="order" :key="order.status"/>

            <order-items :order="order" />
        </div>

        <div v-if="isAdmin" class="text-center py-24" >
            <p v-if="order.status === -1" class="text-2xl text-red-700 opacity-50">--- Cancelled ---</p>

            <div v-else class="flex justify-center gap-x-8">
                <jet-button v-if="showMarkShippedAndInvoiced" class="leading-none" @click="markOrderShippedAndInvoiced">
                    Mark Shipped &amp; Invoiced
                </jet-button>

                <jet-danger-button v-if="order.status === 0 || order.status === 1" @click="showCancelModal = true">
                    Cancel Order
                </jet-danger-button>
            </div>
        </div>

        <jet-confirmation-modal :show="showCancelModal" @close="showCancelModal=false">
            <template #title>
                Cancel Order
            </template>

            <template #content>
                Are you sure you would like to cancel this order?
            </template>

            <template #footer>
                <jet-danger-button @click="cancelOrder">
                    Yes
                </jet-danger-button>

                <jet-tertiary-button class="ml-2" @click="showCancelModal=false">
                    No
                </jet-tertiary-button>
            </template>
        </jet-confirmation-modal>

        <jet-confirmation-modal :show="showMarkShippedAndInvoicedModal" @close="showMarkShippedAndInvoicedModal = false">
            <template #title>
                Mark Order Shipped &amp; Invoiced?
            </template>

            <template #content>
                <div>Are you sure you would like to mark this order shipped &amp; invoiced?</div>
            </template>

            <template #footer>
                <jet-button @click="handleMarkShippedAndInvoiced()">
                    Yes
                </jet-button>

                <jet-tertiary-button class="ml-2" @click="showMarkShippedAndInvoicedModal = false">
                    No
                </jet-tertiary-button>
            </template>
        </jet-confirmation-modal>

        <jet-confirmation-modal :show="showOrderProcessResponseModal" @close="showOrderProcessResponseModal = false">
            <template #title>
                Order Payment Could Not Be Processed
            </template>

            <template #content>
                
                <div v-if="orderProcessResponse == 'could_not_be_processed'">
                    There was an issue processing payment. Please try again.
                </div>
                <div v-else-if="orderProcessResponse == 'invalid'">
                    The customer's credit card on file is invalid. Please contact the customer and update their credit card information before shipping &amp; invoicing.
                </div>
                <div v-else-if="orderProcessResponse == 'missing'">
                    The customer is a credit card customer but doesn't have a credit card on file. Please contact the customer and add their credit card information before shipping &amp; invoicing.
                </div>
                <div v-else-if="orderProcessResponse == 'unknown'">
                    There was an unknown issue when processing payment. 
                </div>

            </template>

            <template #footer>
                <jet-tertiary-button @click="showOrderProcessResponseModal = false">
                    Close
                </jet-tertiary-button>
            </template>
        </jet-confirmation-modal>

        <jet-confirmation-modal :show="showEmailModal" @close="cancelEmail">
            <template #title>
                Email order {{ emailType }}?
            </template>
            <template #footer>
                <jet-danger-button @click="handleEmail">
                    Yes
                </jet-danger-button>

                <jet-tertiary-button class="ml-2" @click="cancelEmail">
                    No
                </jet-tertiary-button>
            </template>
        </jet-confirmation-modal>

    </app-layout>
</template>

<script>
import { usePage } from '@inertiajs/inertia-vue3';
import UseUserAuth from '@/Composables/UseUserAuth';
import AppLayout from '@/Layouts/AppLayout.vue';
import { DateTime } from "luxon";
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetButton from '@/Jetstream/Button';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetDropdown from '@/Jetstream/Dropdown';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import OrderDetails from '@/Shared/Orders/OrderDetails'
import OrderItems from '@/Shared/Orders/OrderItems'

export default {
    setup () {
        const { isAdmin } = UseUserAuth(usePage().props.value.user);

        return { isAdmin }
    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetConfirmationModal,
        JetSecondaryButton,
        JetDangerButton,
        JetDialogModal,
        JetDropdown,
        JetTertiaryButton,
        OrderDetails,
        OrderItems
    },

    props: ['order'],

    data() {
        return {
            showCancelModal: false,
            showEmailModal: false,
            showInvoicedModal: false,
            showMarkShippedAndInvoicedModal: false,
            showOrderProcessResponseModal: false,
            orderProcessResponse: null,
            emailType: null,
            bcSyncProcessing: false,
        }
    },

    methods: {
        async orderSync() {
            this.bcSyncProcessing = true;
            try {
                const response = await axios.post('/business-central/orderSync', {
                    order_id: this.order.id
                });

                console.log(response.data.message);
            } catch (error) {
                console.error(error);
                
                if (error.response && error.response.data && error.response.data.message) {
                    console.error(error.response.data.message);
                } else {
                    console.error('An unknown error occurred.');
                }

                alert('An error occurred while syncing the product. Please check the eror logs.');
            } finally {
                this.bcSyncProcessing = false;
            }
        },

        handlePrint(type) {
            window.open((route('orders.print', { orders: this.order.id, printType: type} )), "_blank");
            if (type == 'confirmation') {
                setTimeout(()=> {
                    this.$inertia.reload();
                }, 250);
            }
            else if (type === 'invoice') {
                this.showMarkShippedAndInvoicedModal = false;

                setTimeout(()=> {
                    this.$inertia.reload();
                }, 250);
            }
        },

        handleRetailPrint(type) {
            let print_route = 'routific.print.' + type;
            if (this.shouldAutoprint(type)) axios.get(route(print_route, { orders: [this.order.id]} ));
            else window.open((route(print_route, { orders: [this.order.id]} )), "_blank");
            if(type === 'invoices') {
                setTimeout(()=> {
                    this.$inertia.reload();
                }, 250);
            }
        },

        cancelOrder () {
            this.$inertia.visit(route('orders.destroy', this.order.id), {method: 'delete'});
        },

        markOrderShippedAndInvoiced() {
            this.showMarkShippedAndInvoicedModal = true;
        },

        handleMarkShippedAndInvoiced() {
            this.showMarkShippedAndInvoicedModal = false;

            axios.post(route('orders.mark.shipped_and_invoiced', { orders: [this.order.id] }))
            .then((response) => {
                let data = response.data;

                if (data.processed.total > 0)
                {
                    this.$inertia.visit(route('orders.index'));
                }
                else
                {
                    if (data.unprocessed.orders.could_not_be_processed.length > 0) this.orderProcessResponse = 'could_not_be_processed';
                    else if (data.unprocessed.orders.invalid.length > 0) this.orderProcessResponse = 'invalid';
                    else if (data.unprocessed.orders.missing.length > 0) this.orderProcessResponse = 'missing';
                    else this.orderProcessResponse = 'unknown';

                    this.showOrderProcessResponseModal = true;
                }
            });
        },

        emailOrder(type) {
            this.emailType = type;
            this.showEmailModal = true;
        },

        handleEmail() {
            this.$inertia.visit(route('orders.email', { order: this.order.id, type: this.emailType }));
        },

        cancelEmail () {
            this.emailType = null;
            this.showEmailModal = false;
        }
    },

    computed: {
        backUrl () {
            const params = route().params;

            if (params.return) {
                return params.return;
            }

            return route('orders.index', {
                page: this.$page.props.page.orders,
            });
        },

        manualPickingModeUrl () {
            const params = route().params;

            return route('orders.edit', {
                order: this.order.id,
                return: params.return,
                manualPickingMode: true,
            });
        },

        editUrl () {
            const params = route().params;

            return route('orders.edit', {
                order: this.order.id,
                return: params.return,
            });
        },

        showMarkShippedAndInvoiced() {
            if (this.order.status !== 0 && this.order.status !== 1) return false;

            return this.canInvoice;
        },

        showEmailButton() {
            if (this.order.status === -1) return false;

            return this.order.status !== 2
        },

        canInvoice() {
            let today = DateTime.now().startOf('day');
            let shipDate = DateTime.fromISO(this.order.ship_date);
            
            if (today < shipDate) return false;
            else return this.order.status == 0 || this.order.status == 1;
        },

        allowEdit() {
            if (!this.order.is_wholesale) {
                if (this.order.status == 0) {
                    return false;
                } else {
                    return true;
                }
            } else {
                return true;
            }
        }
    }
}
</script>

<style scoped>

</style>
